import type React from 'react'
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { HexAlphaColorPicker } from 'react-colorful'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface CanvasColorPickerProps {
  value: string
  opacity?: number
  onChange: (value: string) => void
  onOpacityChange?: (opacity: number) => void
  onBlur?: () => void
}

const hexToRgba = (hex: string) => {
  hex = hex.replace('#', '')

  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  const a = hex?.length === 8 ? parseInt(hex.substring(6, 8), 16) / 255 : 1

  return { r, g, b, a }
}

const rgbaToHex = (r: number, g: number, b: number, a: number = 1) => {
  const rHex = Math.round(r).toString(16).padStart(2, '0')
  const gHex = Math.round(g).toString(16).padStart(2, '0')
  const bHex = Math.round(b).toString(16).padStart(2, '0')

  const aHex =
    a < 1
      ? Math.round(a * 255)
          .toString(16)
          .padStart(2, '0')
      : ''

  return `#${rHex}${gHex}${bHex}${aHex}`.toLowerCase()
}

const getOpacityFromHex = (hex: string) => {
  hex = hex.replace('#', '')
  if (hex.length === 8) {
    return parseInt(hex.substring(6, 8), 16) / 255
  }
  return 1
}

const setOpacityToHex = (hex: string, opacity: number) => {
  const { r, g, b } = hexToRgba(hex)
  return rgbaToHex(r, g, b, opacity)
}

const useForwardedRef = <T,>(ref: React.ForwardedRef<T>) => {
  const innerRef = useRef<T>(null)

  useEffect(() => {
    if (!ref) return
    if (typeof ref === 'function') {
      ref(innerRef.current)
    } else {
      ref.current = innerRef.current
    }
  })

  return innerRef
}

const CanvasColorPicker = forwardRef<
  HTMLInputElement,
  Omit<React.ComponentProps<'button'>, 'value' | 'onChange' | 'onBlur'> & CanvasColorPickerProps
>(
  (
    { disabled, value, opacity = 1, onChange, onOpacityChange, onBlur, name, className, ...props },
    forwardedRef
  ) => {
    const ref = useForwardedRef(forwardedRef)
    const [open, setOpen] = useState(false)

    const initialOpacity = useMemo(() => {
      const opacityFromHex = getOpacityFromHex(value)
      return opacityFromHex !== undefined ? opacityFromHex : opacity
    }, [value, opacity])

    const [opacityInput, setOpacityInput] = useState(() => {
      return initialOpacity === 0 ? '' : `${Math.round(initialOpacity * 100)}%`
    })

    const [tempOpacityInput, setTempOpacityInput] = useState(() => opacityInput)

    const parsedValue = useMemo(() => {
      return value || '#FFFFFF'
    }, [value])

    useEffect(() => {
      const hexOpacity = getOpacityFromHex(value)
      const effectiveOpacity = hexOpacity !== 1 ? hexOpacity : opacity

      const percentage = Math.round(effectiveOpacity * 100)
      const newInput = percentage === 0 ? '' : `${percentage}%`
      setOpacityInput(newInput)
      setTempOpacityInput(newInput)
    }, [value, opacity])

    const handleColorChange = (newColor: string) => {
      if (onChange) {
        onChange(newColor)
      }
    }

    const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setTempOpacityInput(e.target.value)
    }

    const applyOpacityChange = () => {
      if (!tempOpacityInput) {
        setOpacityInput('')

        if (onChange) {
          onChange(setOpacityToHex(parsedValue, 0))
        }

        if (onOpacityChange) {
          onOpacityChange(0)
        }
        return
      }

      const numValue = parseFloat(tempOpacityInput.replace(/%/g, ''))

      if (!isNaN(numValue)) {
        const boundedValue = Math.max(0, Math.min(100, numValue))
        const opacityValue = boundedValue / 100

        const newValue = `${boundedValue}%`
        setOpacityInput(newValue)
        setTempOpacityInput(newValue)

        if (onChange) {
          onChange(setOpacityToHex(parsedValue, opacityValue))
        }

        if (onOpacityChange) {
          onOpacityChange(opacityValue)
        }
      } else {
        setTempOpacityInput(opacityInput)
      }
    }

    return (
      <Popover
        onOpenChange={setOpen}
        open={open}
      >
        <div className='bg-grey-200 flex h-7.5 items-center gap-1.5 rounded-md px-1.5'>
          <PopoverTrigger
            asChild
            disabled={disabled}
            onBlur={onBlur}
          >
            <Button
              {...props}
              className={cn('border-white-100 flex !size-5.5 h-full !shrink-0 rounded-sm border', className)}
              name={name}
              onClick={() => {
                setOpen(true)
              }}
              size='icon'
              style={{
                backgroundColor: parsedValue
              }}
              variant='outline'
            >
              <div />
            </Button>
          </PopoverTrigger>
          <input
            className='h-full w-20 pr-2 text-sm leading-none outline-none'
            maxLength={9}
            onChange={(e) => {
              e.stopPropagation()
              handleColorChange(e?.currentTarget?.value)
            }}
            ref={ref}
            value={parsedValue}
          />
          <div className='border-l-white-100 relative flex h-full w-10.5 items-center border-l pl-1.5'>
            <input
              className='w-full text-sm leading-none outline-none'
              type='text'
              value={tempOpacityInput}
              onChange={handleOpacityChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applyOpacityChange()
                }
              }}
              onBlur={applyOpacityChange}
            />
          </div>
        </div>
        <PopoverContent className='w-full'>
          <HexAlphaColorPicker
            color={parsedValue}
            onChange={handleColorChange}
          />
          <div className='mt-2'>
            <Input
              maxLength={9}
              onChange={(e) => {
                handleColorChange(e?.currentTarget?.value)
              }}
              ref={ref}
              value={parsedValue}
            />
          </div>
        </PopoverContent>
      </Popover>
    )
  }
)
CanvasColorPicker.displayName = 'CanvasColorPicker'

export { CanvasColorPicker }
